export function getLimitationsByPlan(consumptionPlan: ConsumptionPlan): ConsumptionPlanCapabilities {
  switch (consumptionPlan) {
    case ConsumptionPlan.free:
      return {
        maxApplicationsCount: 1,
        maxApiKeysPerAppCount: 1,
        maxRetentionTimeInDays: 7,
        rps: 1,
        maxRequests: 100000,
        maxWeb3Requests: 25000,
        maxUsers: 1,
        price: {
          [BillingIntervalInMonths.One]: 0,
          [BillingIntervalInMonths.Three]: 0,
          [BillingIntervalInMonths.Six]: 0,
          [BillingIntervalInMonths.Twelve]: 0
        }
      };
    case ConsumptionPlan.startup:
      return {
        maxApplicationsCount: 2,
        maxApiKeysPerAppCount: 2,
        maxRetentionTimeInDays: 30,
        rps: 10,
        maxRequests: 1000000,
        maxWeb3Requests: 50000,
        maxUsers: 2,
        price: {
          [BillingIntervalInMonths.One]: 199,
          [BillingIntervalInMonths.Three]: 189,
          [BillingIntervalInMonths.Six]: 179,
          [BillingIntervalInMonths.Twelve]: 149
        }
      };
    case ConsumptionPlan.pro:
      return {
        maxApplicationsCount: 5,
        maxApiKeysPerAppCount: 2,
        maxRetentionTimeInDays: 30,
        maxWeb3Requests: 100000,

        rps: 20,
        maxRequests: 3000000,
        maxUsers: 3,
        price: {
          [BillingIntervalInMonths.One]: 399,
          [BillingIntervalInMonths.Three]: 379,
          [BillingIntervalInMonths.Six]: 359,
          [BillingIntervalInMonths.Twelve]: 299
        }
      };
    case ConsumptionPlan.business:
      return {
        maxApplicationsCount: 10,
        maxApiKeysPerAppCount: 5,
        maxRetentionTimeInDays: 90,
        rps: 40,
        maxRequests: 7000000,
        maxWeb3Requests: 250000,
        maxUsers: 5,
        price: {
          [BillingIntervalInMonths.One]: 799,
          [BillingIntervalInMonths.Three]: 759,
          [BillingIntervalInMonths.Six]: 719,
          [BillingIntervalInMonths.Twelve]: 599
        }
      };
    case ConsumptionPlan.enterprise:
      return {
        maxApplicationsCount: 20,
        maxApiKeysPerAppCount: 10,
        maxRetentionTimeInDays: 180,
        maxWeb3Requests: 250000,
        maxUsers: 20,
        price: {
          [BillingIntervalInMonths.One]: 0,
          [BillingIntervalInMonths.Three]: 0,
          [BillingIntervalInMonths.Six]: 0,
          [BillingIntervalInMonths.Twelve]: 0
        }
      };
    case ConsumptionPlan.internal:
      return {
        maxApplicationsCount: 10,
        maxApiKeysPerAppCount: 10,
        maxRetentionTimeInDays: 1395, // 3 years,
        maxUsers: 10,
        price: {
          [BillingIntervalInMonths.One]: 0,
          [BillingIntervalInMonths.Three]: 0,
          [BillingIntervalInMonths.Six]: 0,
          [BillingIntervalInMonths.Twelve]: 0
        }
      };
    case ConsumptionPlan.custom:
      return {
        maxApplicationsCount: 2,
        maxApiKeysPerAppCount: 2,
        maxRetentionTimeInDays: 30,
        rps: 10,
        maxRequests: 0,
        maxWeb3Requests: 0,
        maxUsers: 2,
        price: {
          [BillingIntervalInMonths.One]: 0,
          [BillingIntervalInMonths.Three]: 0,
          [BillingIntervalInMonths.Six]: 0,
          [BillingIntervalInMonths.Twelve]: 0
        }
      };
    default:
      throw new Error(`Unknown consumption plan: ${consumptionPlan}`);
  }
}

export enum BillingIntervalInMonths {
  One = 1,
  Three = 3,
  Six = 6,
  Twelve = 12
}

export type ConsumptionPlanCapabilities = {
  maxApplicationsCount: number;
  maxApiKeysPerAppCount: number;
  maxRetentionTimeInDays: number;
  maxWeb3Requests?: number;
  rps?: number;
  maxRequests?: number;
  maxUsers: number;
  price: Record<BillingIntervalInMonths, number>;
};

export enum ConsumptionPlan {
  internal = "internal",
  free = "free",
  startup = "startup",
  pro = "pro",
  business = "business",
  enterprise = "enterprise",
  custom = "custom"
}

export const ConsumptionPlanPriority: Record<ConsumptionPlan, number> = {
  [ConsumptionPlan.internal]: 0,
  [ConsumptionPlan.free]: 0,
  [ConsumptionPlan.startup]: 1,
  [ConsumptionPlan.pro]: 2,
  [ConsumptionPlan.business]: 3,
  [ConsumptionPlan.custom]: 4,
  [ConsumptionPlan.enterprise]: 5
};

export enum PaymentStatus {
  paid = "paid",
  sent = "sent",
  past_due = "past_due",
  canceled = "canceled"
}

export enum PaymentType {
  renewal = "renewal",
  downgrade = "downgrade",
  initial = "initial"
}

export enum AssetType {
  crypto = "crypto",
  fiat = "fiat"
}

export enum mailDataType {
  SubscriptionEmail = "SubscriptionEmail",
  SubscriptionCancelationEmail = "SubscriptionCancelationEmail"
}

export type SubscriptionEmail = {
  planName: ConsumptionPlan;
  price: string;
  billingIntervalInMonths: BillingIntervalInMonths;
  startDate: string;
  isOrganization: boolean;
};

export type SubscriptionCancelationEmail = {
  planName: ConsumptionPlan;
  billingIntervalInMonths: BillingIntervalInMonths;
  endDate: string;
  isOrganization: boolean;
};

export type SubscriptionDowngradeEmail = {
  newPlan: ConsumptionPlan;
  previousPlan: ConsumptionPlan;
  billingIntervalInMonths: BillingIntervalInMonths;
  isOrganization: boolean;
  newPlanStartDate: string;
  isPastDue: boolean;
  price?: string;
};

export type SubscriptionUpgradeEmail = {
  newPlan: ConsumptionPlan;
  previousPlan: ConsumptionPlan;
  billingIntervalInMonths: BillingIntervalInMonths;
  isOrganization: boolean;
  newPlanStartDate: string;
};

export type SubscriptionSuspensionEmail = {
  planName: ConsumptionPlan;
  billingIntervalInMonths: BillingIntervalInMonths;
  suspendedDate: string;
  dueSince: string;
  isOrganization: boolean;
  price: string;
  paymentLink: string;
};

export type PaymentReminderEmail = {
  planName: ConsumptionPlan;
  billingIntervalInMonths: BillingIntervalInMonths;
  dueDate: string;
  price: string;
  paymentLink: string;
  paymentNumber: string;
};

export type NoContractNotice = {
  dueDate: string;
};

export type SubscriptionEndTrial = {
  planName: ConsumptionPlan;
  billingIntervalInMonths: BillingIntervalInMonths;
  suspendedDate: string;
  isOrganization: boolean;
};

export const isSupportedBillingInterval = (billingIntervalInMonths: BillingIntervalInMonths): boolean => {
  const supportedBillingIntervals = [
    BillingIntervalInMonths.One,
    BillingIntervalInMonths.Three,
    BillingIntervalInMonths.Six,
    BillingIntervalInMonths.Twelve
  ];
  return supportedBillingIntervals.includes(billingIntervalInMonths);
};

export const planPrice = (plan: ConsumptionPlan, billingIntervalInMonths: BillingIntervalInMonths): number => {
  if (!isSupportedBillingInterval(billingIntervalInMonths)) {
    throw new Error(`Unsupported billing interval: ${billingIntervalInMonths}`);
  }

  const price = getLimitationsByPlan(plan).price[billingIntervalInMonths];
  if (price === undefined) {
    throw new Error(`Price not defined for billing interval: ${billingIntervalInMonths}`);
  }
  return price * billingIntervalInMonths;
};

export const BILLING_INTERVAL_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

export type AdminBillingIntervalInMonths = (typeof BILLING_INTERVAL_OPTIONS)[number];
